// A counterparty's ratings (TX-US-0040, ADR-0013): one to five stars and a comment, one rating
// a person. A BD or Operations sees their own rating in the form and changes it there; everyone
// who reads the counterparty reads them all. A rating is not deleted.
//
// Most recently changed first, twenty to a page. Saving returns to the first page, where the
// rating just given now is, and hands the counterparty's new average to `onRated`.

import { useEffect, useState } from 'react';

import {
  getMyCounterpartyRating,
  pageCounterpartyRatings,
  rateCounterparty,
} from '../../api.js';
import Pager from '../../ui/Pager.jsx';
import usePageTrail from '../../ui/usePageTrail.js';
import StarPicker from './StarPicker.jsx';
import { MAX_COMMENT_LENGTH, raterRoles, starsText } from './ratings.js';

const when = (iso) => new Date(iso).toLocaleString();

export default function CounterpartyRatings({ counterpartyId, canRate, onRated }) {
  const [view, setView] = useState({ cursor: null, reads: 0 });
  const [page, setPage] = useState(null);
  const [mine, setMine] = useState(null);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const trail = usePageTrail({
    query: view,
    page,
    apply: setView,
    withCursor: (current, cursor) => ({ ...current, cursor }),
  });

  useEffect(() => {
    if (!canRate) return;
    getMyCounterpartyRating(counterpartyId)
      .then(({ rating }) => {
        setMine(rating);
        if (rating) {
          setStars(rating.stars);
          setComment(rating.comment);
        }
      })
      .catch((err) => setError(err.message));
  }, [counterpartyId, canRate]);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    pageCounterpartyRatings(counterpartyId, view.cursor)
      .then((found) => {
        if (!cancelled) setPage(found);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [counterpartyId, view]);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { rating, summary } = await rateCounterparty(counterpartyId, stars, comment.trim());
      setMine(rating);
      onRated(summary);
      trail.reset();
      setView((current) => ({ cursor: null, reads: current.reads + 1 }));
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const ratings = page?.ratings ?? [];
  const paged = trail.hasPrevious || Boolean(page?.has_more);
  const unchanged = mine && mine.stars === stars && mine.comment === comment.trim();

  return (
    <section className="counterparty-ratings" aria-labelledby="ratings-heading">
      <h3 id="ratings-heading">Ratings</h3>

      {error && <p className="error">{error}</p>}

      {canRate && (
        <form className="rating-form" onSubmit={save} aria-label="Your rating">
          <p className="muted">
            {mine ? `Your rating, last changed ${when(mine.changed_at)}.` : 'You have not rated them yet.'}
          </p>
          <StarPicker value={stars} onChange={setStars} disabled={busy} />
          <label>
            <span>Why</span>
            <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
                      maxLength={MAX_COMMENT_LENGTH}
                      placeholder="What went well or wrong working with them" />
          </label>
          <button type="submit" disabled={busy || !stars || !comment.trim() || unchanged}>
            {mine ? 'Change my rating' : 'Rate'}
          </button>
        </form>
      )}

      {page && ratings.length === 0 && <p className="muted">No ratings.</p>}

      {ratings.length > 0 && (
        <ol className="rating-list" aria-label="Ratings">
          {ratings.map((rating) => (
            <li key={rating.rating_id}>
              <header>
                <span className="stars" aria-label={`${rating.stars} out of 5 stars`}>
                  {starsText(rating.stars)}
                </span>
                <strong title={rating.rated_by}>{rating.author_name}</strong>
                <span className="tag">{raterRoles(rating)}</span>
                <time dateTime={rating.changed_at}>{when(rating.changed_at)}</time>
              </header>
              <p>{rating.comment}</p>
            </li>
          ))}
        </ol>
      )}

      {paged && (
        <Pager busy={busy} hasPrevious={trail.hasPrevious} hasMore={Boolean(page?.has_more)}
               onPrevious={trail.onPrevious} onNext={trail.onNext} />
      )}
    </section>
  );
}
